/**
 * EntryView — Three-way entry screen: Guest, Login, Sign up.
 * Shown to first-time visitors who are not authenticated.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';

export async function mountEntryView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const layout = createAuthLayout(ctx);
  _injectEntryStyles();

  // Store i18n keys (not pre-translated strings) so we can re-render on lang change
  const CHOICE_DEFS = [
    {
      view: 'guest',
      titleKey: 'auth_entry_guest_button',
      subKey: 'auth_entry_guest_subtitle',
      icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>`,
      kind: 'primary' as const,
    },
    {
      view: 'login',
      titleKey: 'auth_entry_login_button',
      subKey: 'auth_entry_login_subtitle',
      icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`,
      kind: 'secondary' as const,
    },
    {
      view: 'signup',
      titleKey: 'auth_entry_signup_button',
      subKey: 'auth_entry_signup_subtitle',
      icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>`,
      kind: 'secondary' as const,
    },
  ];

  const list = document.createElement('div');
  list.className = 'auth-entry-list';

  function render(): void {
    const t = ctx.i18n.t.bind(ctx.i18n);
    layout.setTitle(t('auth_entry_title'));
    layout.setSubtitle(t('auth_entry_subtitle'));
    list.innerHTML = '';
    for (const def of CHOICE_DEFS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `auth-entry-btn auth-entry-btn-${def.kind}`;
      btn.dataset.view = def.view;
      btn.innerHTML = `
        <span class="auth-entry-icon">${def.icon}</span>
        <span class="auth-entry-text">
          <span class="auth-entry-title">${t(def.titleKey)}</span>
          <span class="auth-entry-sub">${t(def.subKey)}</span>
        </span>
      `;
      btn.addEventListener('click', () => opts.onNavigate(def.view));
      list.appendChild(btn);
    }
  }

  render();
  layout.card.appendChild(list);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  // Re-render text when language changes (aweni:lang-change fires after locale loads)
  const langHandler = () => render();
  window.addEventListener('aweni:lang-change', langHandler);

  return {
    unmount() {
      window.removeEventListener('aweni:lang-change', langHandler);
      container.innerHTML = '';
    },
  };
}

function _injectEntryStyles(): void {
  if (document.getElementById('aweni-entry-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-entry-styles';
  style.textContent = `
    .auth-entry-list {
      display: flex;
      flex-direction: column;
      gap: var(--ln-space-3, 12px);
    }

    .auth-entry-btn {
      display: flex;
      align-items: center;
      gap: var(--ln-space-3-5, 14px);
      width: 100%;
      padding: var(--ln-space-4, 16px) var(--ln-space-4-5, 18px);
      border-radius: var(--ln-radius-md, 14px);
      cursor: pointer;
      font-family: var(--ln-font-ui, inherit);
      text-align: start;
      transition: transform var(--motion-duration-fast) var(--motion-easing-default), filter var(--motion-duration-fast) var(--motion-easing-default), background var(--motion-duration-fast) var(--motion-easing-default);
      min-height: 64px;
    }
    .auth-entry-btn:hover {
      transform: translateY(-1px);
    }
    .auth-entry-btn:active {
      transform: translateY(0);
    }

    .auth-entry-btn-primary {
      background: var(--accent);
      color: var(--accent-text);
      border: none;
    }
    
    .auth-entry-btn-secondary {
      background: var(--bg-secondary);
      color: var(--text-primary);
      border: 1px solid var(--border);
    }
    .auth-entry-btn-secondary:hover {
      background: color-mix(in srgb, var(--text-primary) 4%, var(--bg-secondary));
    }

    .auth-entry-icon {
      flex-shrink: 0;
      width: var(--ln-space-10, 40px);
      height: var(--ln-space-10, 40px);
      border-radius: var(--ln-radius-sm, 10px);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .auth-entry-btn-primary .auth-entry-icon {
      background: color-mix(in srgb, var(--accent-text) 16%, transparent);
      color: var(--accent-text);
    }
    
    .auth-entry-btn-secondary .auth-entry-icon {
      background: color-mix(in srgb, var(--accent) 12%, transparent);
      color: var(--accent);
    }

    .auth-entry-text {
      display: flex;
      flex-direction: column;
      gap: var(--ln-space-0-5, 2px);
      min-width: 0;
    }
    .auth-entry-title {
      font-family: var(--ln-font-ui, inherit);
      font-size: var(--ln-font-size-base, 1rem);
      font-weight: 700;
    }
    .auth-entry-sub {
      font-family: var(--ln-font-ui, inherit);
      font-size: var(--ln-font-size-xs, 0.8rem);
      opacity: 0.85;
    }
    .auth-entry-btn-secondary .auth-entry-sub {
      color: var(--text-secondary);
    }
  `;
  document.head.appendChild(style);
}
